import React, { useEffect, useState, useCallback } from 'react';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, Button, Chip, Avatar, IconButton, TextField, InputAdornment,
    Dialog, DialogTitle, DialogContent, DialogActions, Typography,
    Rating, Box, LinearProgress, Tooltip, Tabs, Tab, Card, CardContent,
    Grid, Divider
} from '@mui/material';
import {
    Search, Edit, Delete, Visibility, Phone, Email,
    Star, TrendingUp, Award, CheckCircle, Download,
    Close as CloseIcon, LocalShipping, AttachMoney, ReviewsOutlined,
    InfoOutlined  // ✅ ADDED: Replace AlertCircle with InfoOutlined
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './DeliveryPersons.css';


const AllDeliveryPersons = () => {
    // ==================== STATE DECLARATIONS ====================
    const [deliveryPersons, setDeliveryPersons] = useState([]);
    const [filteredPersons, setFilteredPersons] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(true);
    const [selectedPerson, setSelectedPerson] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [dialogTabValue, setDialogTabValue] = useState(0);
    const [selectedPersonRatings, setSelectedPersonRatings] = useState([]);
    const [ratingsLoading, setRatingsLoading] = useState(false);
    const [selectedPersonOrders, setSelectedPersonOrders] = useState([]);


    const navigate = useNavigate();


    // ==================== HELPER FUNCTIONS ====================
    const getProfileImageUrl = (person) => {
        if (!person) return null;
        
        if (person.profileImage && typeof person.profileImage === 'object' && person.profileImage.url) {
            return person.profileImage.url;
        }
        
        if (typeof person.profileImage === 'string') {
            if (person.profileImage.startsWith('http')) {
                return person.profileImage;
            }
            return `https://api.rismicmart.com${person.profileImage}`;
        }
        
        return null;
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0
        }).format(amount || 0);
    };


    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };


    const getStatusColor = (status) => {
        switch (status) {
            case 'Available':
                return 'success';
            case 'Busy':
                return 'warning';
            case 'Offline':
                return 'default';
            default:
                return 'default';
        }
    };


    // ==================== FETCH FUNCTIONS ====================
    const fetchDeliveryPersons = async () => {
        try {
            setLoading(true);
            console.log('📥 Fetching delivery persons...');


            const response = await axios.get('https://api.rismicmart.com/api/delivery-person');
            let persons = [];


            if (response.data.success) {
                persons = response.data.data;
            } else if (Array.isArray(response.data)) {
                persons = response.data;
            }


            console.log(`✅ Fetched ${persons.length} delivery persons`);


            const personsWithStats = await Promise.all(
                persons.map(async (person) => {
                    try {
                        console.log(`📊 Fetching stats for ${person.fullName}...`);
                        
                        const statsResponse = await axios.get(
                            `https://api.rismicmart.com/api/orders/delivery-person/${person._id || person.id}/stats`
                        );


                        if (statsResponse.data.success) {
                            const stats = statsResponse.data.data;
                            
                            console.log(`✅ ${person.fullName} Stats:`, {
                                totalEarnings: stats.totalEarnings,
                                totalDeliveries: stats.totalDeliveries,
                                averageRating: stats.averageRating
                            });


                            return {
                                ...person,
                                totalDeliveries: stats.totalDeliveries || 0,
                                earnings: stats.totalEarnings || 0,
                                ratings: {
                                    average: stats.averageRating || 0,
                                    count: stats.totalRatings || 0
                                },
                                completedToday: stats.completedToday || 0,
                                pendingOrders: stats.pendingOrders || 0
                            };
                        } else {
                            console.warn(`⚠️ No stats data for ${person.fullName}`);
                            return {
                                ...person,
                                totalDeliveries: 0,
                                earnings: 0,
                                ratings: { average: 0, count: 0 },
                                completedToday: 0,
                                pendingOrders: 0
                            };
                        }
                    } catch (error) {
                        console.error(`❌ Error fetching stats for ${person.fullName}:`, error.message);
                        return {
                            ...person,
                            totalDeliveries: 0,
                            earnings: 0,
                            ratings: { average: 0, count: 0 },
                            completedToday: 0,
                            pendingOrders: 0
                        };
                    }
                })
            );

            console.log('========================================');
            console.log('✅ ALL DELIVERY PERSONS WITH STATS LOADED');
            console.log('Total Persons:', personsWithStats.length);
            console.log('Total Earnings (All):', personsWithStats.reduce((sum, p) => sum + (p.earnings || 0), 0));
            console.log('========================================');


            setDeliveryPersons(personsWithStats);
            setFilteredPersons(personsWithStats);
        } catch (error) {
            console.error('❌ Error fetching delivery persons:', error);
            alert('Failed to load delivery persons');
        } finally {
            setLoading(false);
        }
    };


    const fetchDeliveryPersonRatings = useCallback(async (deliveryPersonId) => {
        try {
            setRatingsLoading(true);
            console.log('⭐ Fetching ratings for delivery person:', deliveryPersonId);


            const response = await axios.get(
                `https://api.rismicmart.com/api/orders/delivery-person/${deliveryPersonId}`
            );


            let orders = [];
            if (response.data.success) {
                orders = response.data.data || [];
            } else if (Array.isArray(response.data)) {
                orders = response.data;
            }


            const ratedOrders = orders.filter(
                order => order.status === 'delivered' && order.deliveryRated === true
            );


            console.log('✅ Ratings fetched:', ratedOrders.length);
            setSelectedPersonRatings(ratedOrders);
            setSelectedPersonOrders(orders);
        } catch (error) {
            console.error('❌ Error fetching ratings:', error);
            setSelectedPersonRatings([]);
            setSelectedPersonOrders([]);
        } finally {
            setRatingsLoading(false);
        }
    }, []);


    // ==================== ACTION FUNCTIONS ====================
    const handleViewDetails = (person) => {
        setSelectedPerson(person);
        setDialogTabValue(0);
        fetchDeliveryPersonRatings(person._id || person.id);
        setOpenDialog(true);
    };


    const handleViewRatings = (person) => {
        setSelectedPerson(person);
        setDialogTabValue(1);
        fetchDeliveryPersonRatings(person._id || person.id);
        setOpenDialog(true);
    };


    const handleToggleStatus = async (id, currentStatus) => {
        if (window.confirm(
            `Are you sure you want to ${currentStatus ? 'deactivate' : 'activate'} this delivery person?`
        )) {
            try {
                const response = await axios.put(
                    `https://api.rismicmart.com/api/delivery-person/${id}`,
                    { isActive: !currentStatus }
                );


                if (response.data.success) {
                    fetchDeliveryPersons();
                    alert('Status updated successfully!');
                }
            } catch (error) {
                console.error('Error updating status:', error);
                alert('Failed to update status');
            }
        }
    };


    const handleDelete = async (id) => {
        if (window.confirm(
            'Are you sure you want to delete this delivery person? This action cannot be undone.'
        )) {
            try {
                await axios.delete(`https://api.rismicmart.com/api/delivery-person/${id}`);
                fetchDeliveryPersons();
                alert('Delivery person deleted successfully!');
            } catch (error) {
                console.error('Error deleting delivery person:', error);
                alert('Failed to delete delivery person');
            }
        }
    };


    const downloadRatingsReport = (person) => {
        try {
            if (selectedPersonRatings.length === 0) {
                alert('No ratings to download');
                return;
            }

            let csvContent = 'data:text/csv;charset=utf-8,';
            csvContent += 'Delivery Person Ratings Report\n';
            csvContent += `Name,${person.fullName}\n`;
            csvContent += `Phone,${person.phone}\n`;
            csvContent += `Email,${person.email}\n`;
            csvContent += `Gender,${person.gender || 'N/A'}\n`;
            csvContent += `Report Date,${formatDate(new Date())}\n\n`;
            csvContent += 'RATINGS SUMMARY\n';
            csvContent += `Average Rating,${(person.ratings?.average || 0).toFixed(1)}\n`;
            csvContent += `Total Ratings,${selectedPersonRatings.length}\n`;
            csvContent += `Total Deliveries,${person.totalDeliveries}\n`;
            csvContent += `Total Earnings,${formatCurrency(person.earnings)}\n\n`;
            csvContent += 'DETAILED RATINGS\n';
            csvContent += 'Order ID,Customer Rating,Review,Date,Status\n';


            selectedPersonRatings.forEach(rating => {
                const reviewText = (rating.deliveryReview || 'No review').replace(/"/g, '""');
                csvContent += `"#ORD-${rating._id?.slice(-6).toUpperCase()}","${rating.deliveryRating}/5","${reviewText}","${formatDate(rating.deliveryRatedAt)}","${rating.status}"\n`;
            });


            const encodedUri = encodeURI(csvContent);
            const link = document.createElement('a');
            link.setAttribute('href', encodedUri);
            link.setAttribute('download', `ratings_${person.fullName}_${Date.now()}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);


            alert('Report downloaded successfully!');
        } catch (error) {
            console.error('Error downloading report:', error);
            alert('Failed to download report');
        }
    };


    // ==================== USEEFFECTS ====================
    useEffect(() => {
        fetchDeliveryPersons();
    }, []);


    useEffect(() => {
        if (searchQuery) {
            const filtered = deliveryPersons.filter(person =>
                person.fullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                person.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
                person.phone.includes(searchQuery) ||
                (person.vehicleType && person.vehicleType.toLowerCase().includes(searchQuery.toLowerCase())) ||
                (person.gender && person.gender.toLowerCase().includes(searchQuery.toLowerCase()))
            );
            setFilteredPersons(filtered);
        } else {
            setFilteredPersons(deliveryPersons);
        }
    }, [searchQuery, deliveryPersons]);


    const totalEarningsAllPersons = deliveryPersons.reduce((sum, person) => {
        return sum + (person.earnings || 0);
    }, 0);


    console.log('💰 TOTAL EARNINGS (All Delivery Persons):', formatCurrency(totalEarningsAllPersons));


    // ==================== RENDER ====================
    return (
        <div className="delivery-persons-page">
            {/* ==================== PAGE HEADER ==================== */}
            <div className="page-header">
                <h1>All Delivery Persons</h1>
                <div className="header-stats">
                    <div className="stat-card">
                        <span className="stat-label">Total</span>
                        <span className="stat-value">{deliveryPersons.length}</span>
                    </div>
                    <div className="stat-card">
                        <span className="stat-label">Active</span>
                        <span className="stat-value">{deliveryPersons.filter(p => p.isActive).length}</span>
                    </div>
                    <div className="stat-card">
                        <span className="stat-label">Online</span>
                        <span className="stat-value">{deliveryPersons.filter(p => p.status === 'Available').length}</span>
                    </div>
                    <div className="stat-card">
                        <span className="stat-label">Avg Rating</span>
                        <span className="stat-value">
                            ⭐ {(
                                deliveryPersons.reduce((sum, p) => sum + (p.ratings?.average || 0), 0) / Math.max(deliveryPersons.length, 1)
                            ).toFixed(1)}
                        </span>
                    </div>
                    <div className="stat-card">
                        <span className="stat-label">💰 Total Earnings</span>
                        <span className="stat-value" style={{ color: '#007b68', fontWeight: 'bold' }}>
                            {formatCurrency(totalEarningsAllPersons)}
                        </span>
                    </div>
                </div>
            </div>


            {/* ==================== SEARCH SECTION ==================== */}
            <div className="search-section">
                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Search by name, email, phone, vehicle type, or gender..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search />
                            </InputAdornment>
                        ),
                    }}
                />
            </div>

            {/* ==================== TABLE ==================== */}
            <TableContainer component={Paper} className="table-container">
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Profile</TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell>Contact</TableCell>
                            <TableCell>Gender</TableCell>
                            <TableCell>Vehicle</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Active</TableCell>
                            <TableCell>Deliveries</TableCell>
                            <TableCell>Rating</TableCell>
                            <TableCell><strong>Earnings</strong></TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={11} align="center" sx={{ py: 3 }}>
                                    <LinearProgress />
                                    <Typography sx={{ mt: 1 }}>Loading delivery persons and calculating earnings...</Typography>
                                </TableCell>
                            </TableRow>
                        ) : filteredPersons.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={11} align="center">
                                    No delivery persons found
                                </TableCell>
                            </TableRow>
                        ) : (
                            filteredPersons.map((person) => (
                                <TableRow key={person._id || person.id} hover>
                                    <TableCell>
                                        <Avatar
                                            src={getProfileImageUrl(person)}
                                            alt={person.fullName}
                                        >
                                            {person.fullName?.charAt(0) || 'D'}
                                        </Avatar>
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 500 }}>
                                        {person.fullName}
                                    </TableCell>
                                    <TableCell>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                            <span style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <Phone style={{ fontSize: '14px' }} /> {person.phone}
                                            </span>
                                            <span style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <Email style={{ fontSize: '14px' }} /> {person.email}
                                            </span>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={person.gender || 'N/A'} 
                                            size="small"
                                            color={person.gender === 'Male' ? 'primary' : person.gender === 'Female' ? 'secondary' : 'default'}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                            <span>{person.vehicleType || 'N/A'}</span>
                                            {(person.vehicleType === 'Bike' || person.vehicleType === 'Car') && person.vehicleNumber && (
                                                <span style={{ fontSize: '12px', color: '#666' }}>
                                                    {person.vehicleNumber}
                                                </span>
                                            )}
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={person.status || 'Offline'}
                                            color={getStatusColor(person.status)}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={person.isActive ? 'Active' : 'Inactive'}
                                            color={person.isActive ? 'success' : 'error'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>
                                        {person.totalDeliveries || 0}
                                    </TableCell>
                                    <TableCell>
                                        <span style={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            <Star style={{ fontSize: '16px', color: '#ffc107' }} />
                                            {(person.ratings?.average || 0).toFixed(1)}
                                            <span style={{ fontSize: '12px', color: '#999', fontWeight: 'normal' }}>
                                                ({person.ratings?.count || 0})
                                            </span>
                                        </span>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ 
                                            backgroundColor: person.earnings > 0 ? '#e8f5e9' : '#f5f5f5',
                                            padding: '8px 12px',
                                            borderRadius: '8px',
                                            border: person.earnings > 0 ? '1px solid #4caf50' : '1px solid #e0e0e0',
                                            display: 'inline-block'
                                        }}>
                                            <span style={{ 
                                                fontWeight: 700, 
                                                color: person.earnings > 0 ? '#1b5e20' : '#666',
                                                fontSize: '14px' 
                                            }}>
                                                {formatCurrency(person.earnings || 0)}
                                            </span>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                            <Tooltip title="View Details">
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={() => handleViewDetails(person)}
                                                >
                                                    <Visibility fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="View Ratings & Reviews">
                                                <IconButton
                                                    size="small"
                                                    color="warning"
                                                    onClick={() => handleViewRatings(person)}
                                                >
                                                    <Star fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                color={person.isActive ? 'error' : 'success'}
                                                onClick={() => handleToggleStatus(person._id || person.id, person.isActive)}
                                                sx={{ fontSize: '11px' }}
                                            >
                                                {person.isActive ? 'Deactivate' : 'Activate'}
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* ==================== PROFILE DIALOG ==================== */}
            <Dialog
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', py: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar
                            src={getProfileImageUrl(selectedPerson)}
                            sx={{ width: 60, height: 60, border: '3px solid white' }}
                        >
                            {selectedPerson?.fullName?.charAt(0)}
                        </Avatar>
                        <Box>
                            <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                {selectedPerson?.fullName}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                <Chip
                                    label={selectedPerson?.isActive ? 'Active' : 'Inactive'}
                                    color={selectedPerson?.isActive ? 'success' : 'error'}
                                    size="small"
                                    sx={{ color: 'white' }}
                                />
                                <Chip
                                    label={selectedPerson?.status || 'Offline'}
                                    size="small"
                                    sx={{ backgroundColor: 'rgba(255,255,255,0.3)', color: 'white' }}
                                />
                                {selectedPerson?.gender && (
                                    <Chip
                                        label={selectedPerson.gender}
                                        size="small"
                                        sx={{ backgroundColor: 'rgba(255,255,255,0.4)', color: 'white' }}
                                    />
                                )}
                            </Box>
                        </Box>
                    </Box>
                    <IconButton
                        onClick={() => setOpenDialog(false)}
                        sx={{ position: 'absolute', right: 16, top: 16, color: 'white' }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>


                <DialogContent dividers sx={{ p: 0 }}>
                    {selectedPerson && (
                        <>
                            <Tabs
                                value={dialogTabValue}
                                onChange={(e, newValue) => setDialogTabValue(newValue)}
                                variant="fullWidth"
                            >
                                <Tab label="Profile Information" />
                                <Tab label={`Ratings & Reviews (${selectedPersonRatings.length})`} />
                            </Tabs>

                            {dialogTabValue === 0 && (
                                <Box sx={{ p: 3 }}>
                                    {/* Stats Cards */}
                                    <Grid container spacing={2} sx={{ mb: 3 }}>
                                        <Grid item xs={12} sm={4}>
                                            <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                                                <CardContent sx={{ textAlign: 'center' }}>
                                                    <LocalShipping sx={{ fontSize: 48, mb: 1 }} />
                                                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                                        {selectedPerson.totalDeliveries || 0}
                                                    </Typography>
                                                    <Typography variant="body2">Total Deliveries</Typography>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
                                                <CardContent sx={{ textAlign: 'center' }}>
                                                    <Star sx={{ fontSize: 48, mb: 1 }} />
                                                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                                        {(selectedPerson.ratings?.average || 0).toFixed(1)}/5
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        Average Rating ({selectedPerson.ratings?.count || 0})
                                                    </Typography>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
                                                <CardContent sx={{ textAlign: 'center' }}>
                                                    <AttachMoney sx={{ fontSize: 48, mb: 1 }} />
                                                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                                        {formatCurrency(selectedPerson.earnings || 0)}
                                                    </Typography>
                                                    <Typography variant="body2">Total Earnings</Typography>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    </Grid>


                                    {/* Contact and Vehicle Info */}
                                    <Grid container spacing={3}>
                                        <Grid item xs={12} md={6}>
                                            <Card variant="outlined">
                                                <CardContent>
                                                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Phone sx={{ fontSize: 20 }} />
                                                        Contact Information
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                        <Box>
                                                            <Typography variant="caption" color="textSecondary">Email</Typography>
                                                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                                {selectedPerson.email || 'N/A'}
                                                            </Typography>
                                                        </Box>
                                                        <Box>
                                                            <Typography variant="caption" color="textSecondary">Phone</Typography>
                                                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                                {selectedPerson.phone || 'N/A'}
                                                            </Typography>
                                                        </Box>
                                                        <Box>
                                                            <Typography variant="caption" color="textSecondary">Gender</Typography>
                                                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                                {selectedPerson.gender || 'Not specified'}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <Card variant="outlined">
                                                <CardContent>
                                                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <LocalShipping sx={{ fontSize: 20 }} />
                                                        Vehicle Information
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                        <Box>
                                                            <Typography variant="caption" color="textSecondary">Vehicle Type</Typography>
                                                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                                {selectedPerson.vehicleType || 'N/A'}
                                                                {(selectedPerson.vehicleType === 'Battery Cycle' || selectedPerson.vehicleType === 'Battery Bike') && (
                                                                    <Chip 
                                                                        label="⚡ Electric" 
                                                                        size="small" 
                                                                        sx={{ ml: 1, backgroundColor: '#4caf50', color: 'white' }} 
                                                                    />
                                                                )}
                                                            </Typography>
                                                        </Box>
                                                        <Box>
                                                            <Typography variant="caption" color="textSecondary">Vehicle Number</Typography>
                                                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                                {(selectedPerson.vehicleType === 'Bike' || selectedPerson.vehicleType === 'Car') 
                                                                    ? (selectedPerson.vehicleNumber || 'N/A')
                                                                    : (
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#999', fontStyle: 'italic' }}>
                                                                            <InfoOutlined sx={{ fontSize: 16 }} />
                                                                            Not Required
                                                                        </Box>
                                                                    )
                                                                }
                                                            </Typography>
                                                        </Box>
                                                        <Box>
                                                            <Typography variant="caption" color="textSecondary">License Number</Typography>
                                                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                                {(selectedPerson.vehicleType === 'Bike' || selectedPerson.vehicleType === 'Car') 
                                                                    ? (selectedPerson.licenseNumber || 'N/A')
                                                                    : (
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#999', fontStyle: 'italic' }}>
                                                                            <InfoOutlined sx={{ fontSize: 16 }} />
                                                                            Not Required
                                                                        </Box>
                                                                    )
                                                                }
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    </Grid>
                                </Box>
                            )}


                            {/* Tab 2: Ratings */}
                            {dialogTabValue === 1 && (
                                <Box sx={{ p: 3 }}>
                                    {ratingsLoading ? (
                                        <Box sx={{ textAlign: 'center', py: 6 }}>
                                            <LinearProgress />
                                            <Typography sx={{ mt: 2 }}>Loading ratings...</Typography>
                                        </Box>
                                    ) : selectedPersonRatings.length === 0 ? (
                                        <Box sx={{ textAlign: 'center', py: 6 }}>
                                            <Star sx={{ fontSize: 64, color: '#e0e0e0', mb: 2 }} />
                                            <Typography variant="h6" color="textSecondary">No ratings yet</Typography>
                                        </Box>
                                    ) : (
                                        <Box>
                                            {selectedPersonRatings.map((rating, index) => (
                                                <Card key={rating._id || index} sx={{ mb: 2 }}>
                                                    <CardContent>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                                            <Typography sx={{ fontWeight: 600 }}>
                                                                Order #ORD-{rating._id?.slice(-6).toUpperCase()}
                                                            </Typography>
                                                            <Rating value={rating.deliveryRating || 0} readOnly />
                                                        </Box>
                                                        {rating.deliveryReview && (
                                                            <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                                                                <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                                                                    "{rating.deliveryReview}"
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                        <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                                                            {formatDate(rating.deliveryRatedAt)}
                                                        </Typography>
                                                    </CardContent>
                                                </Card>
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </>
                    )}
                </DialogContent>


                <DialogActions sx={{ p: 2 }}>
                    {dialogTabValue === 1 && selectedPersonRatings.length > 0 && (
                        <Button
                            startIcon={<Download />}
                            onClick={() => downloadRatingsReport(selectedPerson)}
                            variant="outlined"
                        >
                            Download Report
                        </Button>
                    )}
                    <Button onClick={() => setOpenDialog(false)} variant="contained">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};


export default AllDeliveryPersons;
